import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Loader2, Upload, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const parseInput = (text) => {
  const trimmed = text.trim();
  if (!trimmed) return [];
  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) return parsed;
    if (typeof parsed === 'object' && parsed !== null) return [parsed];
  } catch { /* fall through */ }
  const objects = [];
  let depth = 0;
  let start = -1;
  for (let i = 0; i < trimmed.length; i++) {
    const ch = trimmed[i];
    if (ch === '{') {
      if (depth === 0) start = i;
      depth++;
    } else if (ch === '}') {
      depth--;
      if (depth === 0 && start !== -1) {
        const chunk = trimmed.slice(start, i + 1);
        try {
          objects.push(JSON.parse(chunk));
        } catch (e) {
          throw new Error(`Failed to parse object at position ${start}: ${e.message}`);
        }
        start = -1;
      }
    }
  }
  return objects;
};

/**
 * Reusable bulk-import modal.
 * @param {object} props
 *   isOpen, onClose, api — standard modal wiring
 *   title — dialog title
 *   description — dialog subtitle
 *   endpoint — backend POST URL (e.g., '/admin/challenge-participants/bulk-import')
 *   payloadKey — key the backend expects ('participants', 'users', 'workouts', etc.)
 *   totalKey — key in the response that reports total count
 *   placeholder — textarea placeholder (JSON example)
 *   testId — root data-testid for the modal
 *   onImportComplete — called after any successful insert
 */
export const GenericBulkImportModal = ({
  isOpen, onClose, api,
  title, description,
  endpoint, payloadKey, totalKey,
  placeholder, testId = 'bulk-import-modal',
  onImportComplete,
}) => {
  const [raw, setRaw] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleImport = async () => {
    let items;
    try {
      items = parseInput(raw);
    } catch (e) {
      toast.error(`Parse error: ${e.message}`);
      return;
    }
    if (!items.length) {
      toast.error('No records found. Paste one or more JSON records.');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await api().post(endpoint, { [payloadKey]: items });
      setResult(res.data);
      toast.success(`Imported ${res.data.inserted_count} / skipped ${res.data.skipped_count} / errors ${res.data.error_count}`);
      if (res.data.inserted_count > 0 && onImportComplete) {
        onImportComplete();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Import failed');
    }
    setLoading(false);
  };

  const handleClose = () => {
    if (loading) return;
    setRaw('');
    setResult(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-2xl bg-card border-border" data-testid={testId}>
        <DialogHeader>
          <DialogTitle className="text-white font-heading uppercase tracking-wide">{title}</DialogTitle>
          <DialogDescription className="text-muted-foreground">{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Textarea
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            placeholder={placeholder}
            className="min-h-[260px] font-mono text-xs bg-black border-border text-white"
            disabled={loading}
            data-testid={`${testId}-textarea`}
          />

          {result && (
            <div className="bg-black/50 border border-border rounded p-3 text-sm space-y-2">
              <div className="flex items-center gap-2 text-green-400">
                <CheckCircle2 className="h-4 w-4" />
                <span>Inserted: <strong>{result.inserted_count}</strong></span>
              </div>
              <div className="text-muted-foreground">Skipped: <strong>{result.skipped_count}</strong></div>
              {result.error_count > 0 && (
                <div className="flex items-start gap-2 text-red-400">
                  <AlertTriangle className="h-4 w-4 mt-0.5" />
                  <div>
                    <div>Errors: <strong>{result.error_count}</strong></div>
                    <ul className="mt-1 text-xs list-disc list-inside">
                      {result.errors.slice(0, 10).map((e) => (
                        <li key={`err-${e.index}-${e.error?.slice(0, 16)}`}>row {e.index}: {e.error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              <div className="text-muted-foreground text-xs">
                Total in DB: {result[totalKey]}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button onClick={handleClose} variant="outline" disabled={loading} data-testid={`${testId}-cancel-btn`}>
              Close
            </Button>
            <Button
              onClick={handleImport}
              disabled={loading || !raw.trim()}
              className="bg-primary text-black hover:bg-primary/90"
              data-testid={`${testId}-submit-btn`}
            >
              {loading ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Importing...</>
              ) : (
                <><Upload className="h-4 w-4 mr-2" /> Import</>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
